import {
  Button,
  ConfirmDialog,
  InlineMessage,
  PageContent,
  PageHeader,
  ProfileEditor,
  SegmentedButtonGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingRow,
  SettingsGroup,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { useDocumentTitle } from '../documentTitle';
import { useAppearanceChoice } from '../appearance/useAppearanceChoice';
import { photoInputError } from '../profile/profileModel';
import { useMyProfile } from '../profile/ProfileProvider';
import {
  chooseWeekStart,
  WEEK_START_OPTIONS,
  type WeekStartChoice,
  weekStartDay,
} from './weekStartChoice';

/**
 * Your settings: the signed-in person's own profile and preferences, built
 * like studio-fripp's from @olytic/foundation's settings components.
 *
 * Name, title, about and the photo save to /api/me/profile and /api/me/photo.
 * Week starts on saves the moment it is picked (/api/me/preferences).
 * Appearance is kept in this browser by design. Rows that do not apply to ops
 * yet are disabled as "Not available yet" rather than editable and lost.
 */

const SAVED_FOR_MS = 2000;
const NOT_AVAILABLE = 'Not available yet';
const ignore = () => undefined;

const message = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export function SettingsPage(): JSX.Element {
  useDocumentTitle('Settings');
  const { email, profile, loadError, avatarUrl, saveProfile, saveWeekStart, uploadPhoto, removePhoto, signOut } =
    useMyProfile();
  const [appearance, setAppearance] = useAppearanceChoice(email);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const resolvedWeekStart = weekStartDay(profile?.weekStartsOn);
  const [weekStart, setWeekStart] = useState<WeekStartChoice>({
    value: resolvedWeekStart,
    status: 'idle',
    error: null,
  });

  useEffect(() => {
    setWeekStart((current) => (current.status === 'saving' ? current : { ...current, value: resolvedWeekStart }));
  }, [resolvedWeekStart]);

  useEffect(() => {
    if (weekStart.status !== 'saved') return;
    const timer = setTimeout(
      () => setWeekStart((current) => (current.status === 'saved' ? { ...current, status: 'idle' } : current)),
      SAVED_FOR_MS,
    );
    return () => clearTimeout(timer);
  }, [weekStart.status]);

  async function onSave(next: { displayName: string; title?: string | undefined; about?: string | undefined }) {
    setSaving(true);
    setSaveError(null);
    try {
      // The editor resets to the stored profile and shows "All changes saved".
      await saveProfile({ displayName: next.displayName, title: next.title ?? null, about: next.about ?? null });
    } catch (error) {
      setSaveError(message(error, 'Your profile was not saved.'));
    } finally {
      setSaving(false);
    }
  }

  async function onAvatarChange(file: File | undefined) {
    setSaveError(null);
    if (file !== undefined) {
      const problem = photoInputError(file);
      if (problem !== null) {
        setSaveError(problem);
        return;
      }
    }
    setAvatarUploading(true);
    try {
      await (file === undefined ? removePhoto() : uploadPhoto(file));
    } catch (error) {
      setSaveError(message(error, 'Your photo was not saved.'));
    } finally {
      setAvatarUploading(false);
    }
  }

  return (
    <PageContent as="section" width="default">
      <Stack>
        <PageHeader className="border-0 bg-transparent px-0 pt-0 pb-0" eyebrow="Account" title="Your settings" />

        <Tabs className="flex flex-col gap-5" defaultValue="profile">
          <TabList>
            <Tab value="profile">Profile</Tab>
            <Tab value="preferences">Preferences</Tab>
          </TabList>

          <TabPanel value="profile">
            {loadError !== null ? (
              <InlineMessage tone="error" role="alert">
                {loadError}
              </InlineMessage>
            ) : profile === null ? (
              <Spinner label="Loading your profile…" />
            ) : (
              <ProfileEditor
                  value={{
                    displayName: profile.displayName,
                    title: profile.title ?? undefined,
                    about: profile.about ?? undefined,
                    avatarUrl,
                  }}
                  onSave={(next) => void onSave(next)}
                  onAvatarChange={(file) => void onAvatarChange(file)}
                  saving={saving}
                  avatarUploading={avatarUploading}
                  error={saveError ?? undefined}
                  aboutPlaceholder="What you work on, and anything the others should know."
                  facts={[
                    { label: 'Email', value: email },
                    { label: 'Signs in with', value: 'Google' },
                  ]}
                />
            )}
          </TabPanel>

          <TabPanel value="preferences" className="flex flex-col gap-5">
            <SettingsGroup title="Display" eyebrow="Preferences">
              <SettingRow
                label="Appearance"
                description="Kept in this browser. System follows your device. The Olytic theme has no dark colours yet."
                control={
                  <SegmentedButtonGroup
                    label="Appearance"
                    size="sm"
                    value={appearance}
                    onChange={setAppearance}
                    options={[
                      { value: 'light', label: 'Light' },
                      { value: 'dark', label: 'Dark' },
                      { value: 'system', label: 'System' },
                    ]}
                  />
                }
              />
            </SettingsGroup>

            <SettingsGroup title="Calendar" eyebrow="Preferences">
              <SettingRow
                label="Week Starts on"
                description={
                  weekStart.error !== null ? (
                    <InlineMessage tone="error" role="alert">
                      {weekStart.error}
                    </InlineMessage>
                  ) : weekStart.status === 'saved' ? (
                    <span role="status">Saved</span>
                  ) : (
                    'The first day shown in calendars and reports.'
                  )
                }
                disabled={profile === null}
                control={
                  <SegmentedButtonGroup
                    label="Week Starts on"
                    size="sm"
                    value={weekStart.value}
                    onChange={(next) => {
                      if (profile === null) return;
                      void chooseWeekStart(
                        weekStart,
                        next,
                        async (value) => {
                          await saveWeekStart(value);
                        },
                        setWeekStart,
                      );
                    }}
                    options={WEEK_START_OPTIONS}
                  />
                }
              />
              <SettingRow
                label="Time Zone"
                description={NOT_AVAILABLE}
                disabled
                control={
                  <Select value="America/New_York" disabled>
                    <SelectTrigger className="w-52" aria-label="Time zone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/New_York">New York</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
            </SettingsGroup>

            <SettingsGroup title="Assistant" eyebrow="Preferences">
              <SettingRow
                label="Open the assistant when I sign in"
                description={NOT_AVAILABLE}
                disabled
                control={(id) => <Switch aria-labelledby={id} checked={false} disabled onCheckedChange={ignore} />}
              />
            </SettingsGroup>

            <SettingsGroup title="Session" eyebrow="Account">
              <SettingRow
                label="Sign out"
                description="End your session on this device."
                control={
                  <Button variant="neutral" size="sm" onClick={() => setSigningOut(true)}>
                    Sign out
                  </Button>
                }
              />
            </SettingsGroup>
          </TabPanel>
        </Tabs>

        <ConfirmDialog
          open={signingOut}
          onOpenChange={setSigningOut}
          title="Sign out?"
          description="You will need to sign in again on this device."
          confirmLabel="Sign out"
          onConfirm={() => {
            setSigningOut(false);
            void signOut();
          }}
        />
      </Stack>
    </PageContent>
  );
}
